import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { eventBus } from './EventBus.js';

export class SceneManager {
    constructor(canvas) {
    this.canvas = canvas;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0f172a);
    this.currentView = 'overview';
    this._topDownHeight = 12;
    
    // Camera configurations
    this.cameraConfig = {
        // Default/reset view position
        default: {
            position: new THREE.Vector3(-12.5, 3, 8),
            target: new THREE.Vector3(-0.5, 2, -2),
            up: new THREE.Vector3(0, 1, 0)
        },
        // Top-down view position
        topDown: {
            position: new THREE.Vector3(0, 30, 0),
            target: new THREE.Vector3(0, 0, 0),
            up: new THREE.Vector3(0, 0, -1)
        }
    };
    
    // Camera
    this.camera = new THREE.PerspectiveCamera(
        75, 
        window.innerWidth / window.innerHeight, 
        0.1, 
        1000
    );
    
    // Apply default position
    this.camera.position.copy(this.cameraConfig.default.position);
    
    // Store initial for reset
    this.initialCameraPosition = this.cameraConfig.default.position.clone();
    this.initialTarget = this.cameraConfig.default.target.clone();
    this.isAnimating = false;

        // Renderer
        this.renderer = new THREE.WebGLRenderer({ 
            canvas: this.canvas, 
            antialias: true,
            alpha: false
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        
        // Controls
        this.controls = new OrbitControls(this.camera, this.canvas);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;

        // Tone mapping for HDR
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 0.8;
        
        // Load HDRI (lighting only, invisible background)
        this.loadEnvironment('./img/forest.exr');
        
        // Lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        this.scene.add(ambientLight);
        
        const dirLight = new THREE.DirectionalLight(0xffffff, 1);
        dirLight.position.set(5, 10, 5);
        this.scene.add(dirLight);

        // Grid floor for orientation
        this.createGridFloor();
        
        // X-Ray mode state
        this.isXRay = false;
        this.architectureMeshes = [];
        this.originalMaterials = new Map();
        this.hologramMaterial = this.createHologramMaterial();
        
        // Slot storage
        this.slots = new Map();
        this.furnitureMeshes = []; // Add this line
        
        // Resize handler
        this.setupResize();

        // Eventbus Subscriptions
        eventBus.on('camera:topdown', () => this.setTopDownView());
        eventBus.on('camera:reset', () => this.resetView());
        eventBus.on('xray:toggle', (isActive) => {
            if (isActive !== this.isXRay) this.toggleXRay();
        });
    }

    async loadEnvironment(exrPath) {
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        pmremGenerator.compileEquirectangularShader();
        
        new EXRLoader().load(exrPath, (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            const envMap = pmremGenerator.fromEquirectangular(texture).texture;
            
            this.scene.environment = envMap;           // ← LIGHTING ONLY
            this.scene.background = new THREE.Color(0x0f172a);  // ← YOUR DARK BLUE
            
            texture.dispose();
            pmremGenerator.dispose();
        });
    }
    
    createHologramMaterial() {
        return new THREE.ShaderMaterial({
            uniforms: {
                color: { value: new THREE.Color(0x06b6d4) }, // Cyan
                rimStrength: { value: 2.0 },
                opacity: { value: 0.15 },
                time: { value: 0 }
            },
            vertexShader: `
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                
                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    vViewPosition = -mvPosition.xyz;
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform vec3 color;
                uniform float rimStrength;
                uniform float opacity;
                uniform float time;
                
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                
                void main() {
                    vec3 normal = normalize(vNormal);
                    vec3 viewDir = normalize(vViewPosition);
                    
                    // Fresnel effect
                    float fresnel = pow(1.0 - abs(dot(normal, viewDir)), rimStrength);
                    
                    // Subtle pulse
                    float pulse = sin(time * 2.0) * 0.2 + 0.8;
                    
                    vec3 glow = color * fresnel * pulse;
                    float alpha = fresnel * opacity * 3.0 * pulse;
                    
                    gl_FragColor = vec4(glow, alpha);
                }
            `,
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });
    }
    
    createGridFloor() {
        const gridSize = 35;
        const gridDivisions = 20;
        
        const gridHelper = new THREE.GridHelper(gridSize, gridDivisions, 0x06b6d4, 0x1e293b);
        gridHelper.position.y = 0;
        gridHelper.material.transparent = true;
        gridHelper.material.opacity = 0.3;
        gridHelper.name = 'orientationGrid';
        
        this.scene.add(gridHelper);
        this.gridFloor = gridHelper;
        
        console.log('Grid floor added');
    }

    async loadArchitecture(path) {
        const loader = new GLTFLoader();
        
        return new Promise((resolve, reject) => {
            loader.load(path, (gltf) => {
                this.room = gltf.scene;
                this.scene.add(this.room);
                
                // Extract slots and catalog architecture
                this.room.traverse((child) => {
                    // Find slots (unchanged)
                    if (child.name && child.name.startsWith('slot_')) {
                        const worldPos = new THREE.Vector3();
                        child.getWorldPosition(worldPos);
                        this.slots.set(child.name, worldPos);
                        child.visible = false;
                    }
                    
                    // Architecture handling
                    if (child.isMesh && child.name.startsWith('ARCH_')) {
                        const nameLower = child.name.toLowerCase();
                        const isWall = nameLower.includes('wall');
                        
                        // Clone material for all architecture
                        const clonedMaterial = child.material.clone();
                        
                        if (isWall) {
                            // WALLS: Explicitly force SingleSide
                            clonedMaterial.side = THREE.FrontSide; // or THREE.SingleSide (same thing)
                        } else {
                            // FLOOR/ROOF: DoubleSide (visible from all angles)
                            clonedMaterial.side = THREE.DoubleSide;
                        }
                        
                        child.material = clonedMaterial;
                        
                        // X-RAY TRACKING: ALL arch objects enter the holographic system
                        this.architectureMeshes.push(child);
                        this.originalMaterials.set(child, clonedMaterial);
                    }
                    
                    // Furniture (unchanged)
                    if (child.isMesh && child.name.startsWith('FURN_')) {
                        this.furnitureMeshes.push(child);
                    }
                });
                
                console.log(`Loaded architecture with ${this.slots.size} slots and ${this.architectureMeshes.length} wall meshes`);
                resolve();
            }, undefined, reject);
        });
    }

    toggleXRay() {
        this.isXRay = !this.isXRay;

        this.architectureMeshes.forEach((mesh) => {
            if (this.isXRay) {
                mesh.material = this.hologramMaterial;
            } else {
                mesh.material = this.originalMaterials.get(mesh);
            }
        });

        this.furnitureMeshes.forEach((mesh) => {
            mesh.visible = !this.isXRay;
        });

        // ADD THIS LINE: Notify UI to sync checkbox state
        eventBus.emit('xray:stateChanged', this.isXRay);

        return this.isXRay;
    }

    // Reset to initial overview
    
    setTopDownView(
        position = this.cameraConfig.topDown.position, target = this.cameraConfig.topDown.target, up = this.cameraConfig.topDown.up) {
            if (this.isAnimating || this.currentView === 'topdown') return;

            this.isAnimating = true;
            this.controls.enabled = false;
            this.currentView = 'topdown';

            const startPos = this.camera.position.clone();
            const startQuat = this.camera.quaternion.clone();

            // Calculate exact end rotation based on position, target, and up vector
            const endQuat = new THREE.Quaternion().setFromRotationMatrix(
                new THREE.Matrix4().lookAt(position, target, up)
            );

            let progress = 0;
            const animate = () => {
                progress++;
                const t = progress / 60;
                const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

                // Lerp position to exact specified location
                this.camera.position.lerpVectors(startPos, position, ease);

                // Slerp rotation to exact specified orientation
                this.camera.quaternion.slerpQuaternions(startQuat, endQuat, ease);

                if (progress < 60) {
                    requestAnimationFrame(animate);
                } else {
                    // Exact final position and rotation (existing code)
                    this.camera.position.copy(position);
                    this.camera.quaternion.copy(endQuat);
                    this.camera.up.copy(up);
                
                    this.controls.enabled = false;
                    this.controls.enableRotate = false;
                    this.controls.enablePan = false;
                    this.controls.enableZoom = false;
                
                    this.isAnimating = false;
                    this._topDownHeight = position.y;
                    this._enableTopDownControls();
                    
                    // Auto-enable X-ray for top-down view (only if architecture loaded)
                    if (!this.isXRay && this.architectureMeshes.length > 0) {
                        this.toggleXRay();
                    }
                }
            };
            animate();
    }

    _enableTopDownControls() {
        this._tdOnWheel = (e) => {
            e.preventDefault();
            const zoomSpeed = 0.01;
            this._topDownHeight = THREE.MathUtils.clamp(
                this._topDownHeight + e.deltaY * zoomSpeed,
                5, 40
            );
            this.camera.position.setY(this._topDownHeight);
            this.camera.lookAt(
                this.camera.position.x,
                0,
                this.camera.position.z
            );
        };

        this._tdIsDragging = false;
        this._tdLastPointer = new THREE.Vector2();

        this._tdOnPointerDown = (e) => {
            this._tdIsDragging = true;
            this._tdLastPointer.set(e.clientX, e.clientY);
        };

        this._tdOnPointerMove = (e) => {
            if (!this._tdIsDragging) return;

            const panSpeed = this._topDownHeight / window.innerHeight;
            const dx = (e.clientX - this._tdLastPointer.x) * panSpeed;
            const dz = (e.clientY - this._tdLastPointer.y) * panSpeed;

            this.camera.position.x -= dx;
            this.camera.position.z -= dz;
            this.camera.lookAt(
                this.camera.position.x,
                0,
                this.camera.position.z
            );

            this._tdLastPointer.set(e.clientX, e.clientY);
        };

        this._tdOnPointerUp = () => { this._tdIsDragging = false; };

        // Touch pinch-to-zoom support for mobile
        this._activeTouches = new Map();
        this._lastPinchDistance = 0;

        this._tdOnTouchStart = (e) => {
            if (e.touches.length === 2) {
                // Start of pinch
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                this._lastPinchDistance = Math.sqrt(dx * dx + dy * dy);
            }
            // Store touches for tracking
            for (let i = 0; i < e.touches.length; i++) {
                this._activeTouches.set(e.touches[i].identifier, {
                    x: e.touches[i].clientX,
                    y: e.touches[i].clientY
                });
            }
        };

        this._tdOnTouchMove = (e) => {
            e.preventDefault(); // Prevent page scroll

            if (e.touches.length === 2 && this._lastPinchDistance > 0) {
                // Pinch zoom
                const dx = e.touches[0].clientX - e.touches[1].clientX;
                const dy = e.touches[0].clientY - e.touches[1].clientY;
                const distance = Math.sqrt(dx * dx + dy * dy);

                const delta = distance - this._lastPinchDistance;
                const zoomSpeed = 0.05;

                this._topDownHeight = THREE.MathUtils.clamp(
                    this._topDownHeight - delta * zoomSpeed,
                    5, 40
                );
                this.camera.position.setY(this._topDownHeight);
                this.camera.lookAt(
                    this.camera.position.x,
                    0,
                    this.camera.position.z
                );

                this._lastPinchDistance = distance;
            }
        };

        this._tdOnTouchEnd = (e) => {
            // Clear ended touches
            for (let i = 0; i < e.changedTouches.length; i++) {
                this._activeTouches.delete(e.changedTouches[i].identifier);
            }
            // Reset pinch tracking if less than 2 fingers
            if (e.touches.length < 2) {
                this._lastPinchDistance = 0;
            }
        };

        this.canvas.addEventListener('wheel', this._tdOnWheel, { passive: false });
        this.canvas.addEventListener('pointerdown', this._tdOnPointerDown);
        window.addEventListener('pointermove', this._tdOnPointerMove);
        window.addEventListener('pointerup', this._tdOnPointerUp);

        // Touch events for pinch zoom
        this.canvas.addEventListener('touchstart', this._tdOnTouchStart, { passive: false });
        this.canvas.addEventListener('touchmove', this._tdOnTouchMove, { passive: false });
        this.canvas.addEventListener('touchend', this._tdOnTouchEnd, { passive: false });
        this.canvas.addEventListener('touchcancel', this._tdOnTouchEnd, { passive: false });
    }

    _disableTopDownControls() {
        if (this._tdOnWheel) {
            this.canvas.removeEventListener('wheel', this._tdOnWheel);
            this.canvas.removeEventListener('pointerdown', this._tdOnPointerDown);
            window.removeEventListener('pointermove', this._tdOnPointerMove);
            window.removeEventListener('pointerup', this._tdOnPointerUp);

            // Remove touch listeners
            this.canvas.removeEventListener('touchstart', this._tdOnTouchStart);
            this.canvas.removeEventListener('touchmove', this._tdOnTouchMove);
            this.canvas.removeEventListener('touchend', this._tdOnTouchEnd);
            this.canvas.removeEventListener('touchcancel', this._tdOnTouchEnd);

            this._tdOnWheel = null;
            this._activeTouches = null;
            this._lastPinchDistance = 0;
        }
    }

    resetView() {
        if (this.isAnimating) return;

        if (this.currentView === 'topdown') {
            this._disableTopDownControls();
        }

        this.isAnimating = true;
        this.currentView = 'overview';

        const startPos = this.camera.position.clone();
        const startQuat = this.camera.quaternion.clone();

        // Use config for target position
        const endPos = this.cameraConfig.default.position.clone();
        const endQuat = new THREE.Quaternion().setFromRotationMatrix(
            new THREE.Matrix4().lookAt(
                endPos, 
                this.cameraConfig.default.target, 
                this.cameraConfig.default.up
            )
        );

        let progress = 0;
        const animate = () => {
            progress++;
            const t = progress / 60;
            const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            // Lerp position
            this.camera.position.lerpVectors(startPos, endPos, ease);
            // Slerp rotation
            this.camera.quaternion.slerpQuaternions(startQuat, endQuat, ease);

            if (progress < 60) {
                requestAnimationFrame(animate);
            } else {
                // Exact final position and rotation
                this.camera.position.copy(endPos);
                this.camera.quaternion.copy(endQuat);
                this.camera.up.set(0, 1, 0); // Restore up vector that top-down may have changed
            
                this.controls.dispose();
                this.controls = new OrbitControls(this.camera, this.canvas);
                this.controls.enableDamping = true;
                this.controls.dampingFactor = 0.05;
                this.controls.target.copy(this.cameraConfig.default.target);
                this.controls.update(); // Safe: fresh instance, no stale state
            
                this.isAnimating = false;
            }
        };
        animate();
    }

    animateCamera(endPosition, endTarget, onComplete) {
        const startPosition = this.camera.position.clone();
        const startTarget = this.controls.target.clone();
        let progress = 0;
        const duration = 60;

        const animate = () => {
            progress++;
            const t = progress / duration;
            const ease = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

            this.camera.position.lerpVectors(startPosition, endPosition, ease);
            this.controls.target.lerpVectors(startTarget, endTarget, ease);
            this.controls.update();

            if (progress < duration) {
                requestAnimationFrame(animate);
            } else {
                console.log('=== BEFORE SNAP FIX ===');

                // Don't enable controls yet - let the callback handle it
                this.isAnimating = false;

                if (onComplete) {
                    console.log('=== RUNNING CALLBACK ===');
                    onComplete(); // Callback will enable controls
                    console.log('=== AFTER CALLBACK ===');
                }
            }
        };
        animate();
    }

    toggleView() {
        if (this.currentView === 'overview') {
            this.setTopDownView();
        } else {
            this.resetView();
        }
        return this.currentView;
    }

    getSlotPosition(slotId) {
        return this.slots.get(slotId);
    }
    
    getAllSlotPositions() {
        const positions = [];
        this.slots.forEach((position, name) => {
            positions.push({ name, position });
        });
        return positions;
    }
    
    setupResize() {
        window.addEventListener('resize', () => {
            this.camera.aspect = window.innerWidth / window.innerHeight;
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(window.innerWidth, window.innerHeight);
        });
    }
    
    update() {
        // Defensive X-ray animation with multiple guards
        if (this.isXRay) {
            if (this.hologramMaterial && 
                this.hologramMaterial.uniforms && 
                this.hologramMaterial.uniforms.time &&
                typeof this.hologramMaterial.uniforms.time.value === 'number') {
                this.hologramMaterial.uniforms.time.value += 0.016;
            }
        }
    
        if (!this.isAnimating && this.currentView !== 'topdown') {
            this.controls.update();
        }
        this.renderer.render(this.scene, this.camera);
    }
}